import React, { forwardRef, useState, useEffect } from 'react';
import { NAMESPACE, useLang } from '@/hooks/use-lang';
import { ManualEntryRow } from './manual-entry-row';
import { gridClassName } from './constants';
import { Button, GlobalLoading, Alert } from '@/shared/components';
import { useGetProductByCatalogId } from '@/hooks/quick-order.hook';
import { useRouter } from 'next/navigation';
import ClickAwayListener from '@mui/base/ClickAwayListener';
import { BulkUploadContext } from './quick-order';
import { useEventTracking } from '@/shared/tracking/use-tracking';

interface IProps {
  manualDataFunc: (value) => void;
}
const ManualEntry = forwardRef<HTMLInputElement, IProps>(function BulkUpload(props, ref) {
  const { manualDataFunc } = props;
  const { t } = useLang(NAMESPACE.quickOrder);
  const { skuData, skuMethod } = React.useContext(BulkUploadContext);
  const router = useRouter();
  const defaultCount = 3;
  const [rowCount, setRowCount] = useState(defaultCount);
  const [productData, setProductData] = useState<any>(skuData);
  const [productQuantity, setProductQuantity] = useState<any>([0, 0, 0]);
  const [skuAdded, setSkuAdded] = useState<boolean>(false);
  const [clearAllClicked, setClearAll] = useState<boolean>(false);
  const [deleteRowIndex, setDeleteRowIndex] = useState([]);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [updatedSkuData, setUpdatedSkuData] = useState<any>(skuData);
  const getProductByCatalogId = useGetProductByCatalogId();
  const maxQuantity = 9999;
  useEffect(() => {
    if (skuData && skuData.length > 0 && !loading) {
      setLoading(true);
    }
  }, [skuData]);
  useEffect(() => {
    if (updatedSkuData && updatedSkuData.length > 0 && loading) {
      const tempData = updatedSkuData.filter((data) => data.childSku !== undefined && data.childSku !== '');
      const prodData = [...tempData];
      const quantityData = [...productQuantity];
      let combinedData = [...tempData];
      if (tempData.length > defaultCount) {
        combinedData = tempData.reduce((accumulator, current) => {
          const existingItem = accumulator.find((item) => item.childSku === current.childSku);
          const quantity = parseInt(current.quantity, 10);
          if (isNaN(quantity) || quantity === null || quantity === undefined) {
            current.quantity = 1;
          }
          if (existingItem) {
            existingItem.quantity += parseInt(current.quantity, 10);
            existingItem.quantity = existingItem.quantity > maxQuantity ? maxQuantity : existingItem.quantity;
          } else {
            accumulator.push({
              childSku: current.childSku,
              quantity: parseInt(current.quantity, 10) > maxQuantity ? maxQuantity : parseInt(current.quantity, 10),
            });
          }
          return accumulator;
          //}
        }, []);
        for (let i = rowCount; i < combinedData.length; i++) {
          prodData.push({});
        }
        setRowCount(combinedData.length);
      }
      const fetchCatalogData = async () => {
        try {
          GlobalLoading.show(t('loading.msg'));

          const catalogData = await Promise.all(
            combinedData?.map((sku) => {
              if (sku?.childSku) {
                return getProductByCatalogId.mutateAsync(sku?.childSku);
              }
            })
          );
          if (catalogData) {
            GlobalLoading.hide();
            prodData.map((data, index) => {
              if (data && data.catalogNumber) {
                return;
              } else {
                if (catalogData[index]) {
                  const obj =
                    catalogData[index].code === '200'
                      ? catalogData[index]?.data[0]
                      : { error: catalogData[index].message, catalogNumber: combinedData[index].childSku };
                  prodData[index] = obj;
                  quantityData[index] = parseInt(combinedData[index].quantity, 10);
                }
              }
            });
            setProductData(prodData);
            setProductQuantity(quantityData);
          }
        } catch (error) {
          GlobalLoading.hide();
        }
      };
      fetchCatalogData();
    }
  }, [loading]);
  const updateMasterData = () => {
    const masterData = [];
    productData.map((data, index) => {
      const obj = {
        childSku: data?.catalogNumber,
        quantity: productQuantity[index],
      };

      masterData[index] = obj;
    });
    manualDataFunc(masterData);
  };
  useEffect(() => {
    updateMasterData();
  }, [productQuantity]);
  useEffect(() => {
    let hasData = false;
    productData.map((data) => {
      if (data && data.catalogNumber && !data.error) {
        hasData = true;
      }
    });
    setSkuAdded(hasData);
    updateMasterData();
  }, [productData]);
  const addMoreRows = () => {
    const count = rowCount + defaultCount;
    setRowCount(count);
    const updatedProductData = [...productData];
    for (let i = rowCount; i < count; i++) {
      updatedProductData.push({});
    }
    setProductData(updatedProductData);
  };
  const deleteData = (key) => {
    const updatedData = [...productData];
    updatedData[key] = {};
    setProductData(updatedData);
    const quantityData = [...productQuantity];
    quantityData[key] = 0;
    setProductQuantity(quantityData);
  };
  const clearAll = () => {
    setProductData([{}, {}, {}]);
    setProductQuantity([0, 0, 0]);
    setClearAll(true);
    setRowCount(defaultCount);
  };

  const addProductData = (data, quantity, index) => {
    const updatedData = [...productData];
    updatedData[index] = data;
    setProductData(updatedData);
    updateQuantity(index, quantity);
  };
  const updateQuantity = (key, quantity) => {
    const quantityData = [...productQuantity];
    quantityData[key] = quantity;
    setProductQuantity(quantityData);
  };

  const { trackEvent } = useEventTracking();

  const addToCart = async () => {
    const productArr = [],
      errArr = [];
    productData.map((data, index) => {
      if (data && data.catalogNumber && !data.error) {
        const obj = { catalogNumber: data.catalogNumber, quantity: productQuantity[index] };
        data.quantity = productQuantity[index];
        productArr.push(obj);
      }
      if (data.error) {
        errArr.push(data.error);
      }
    });
    GlobalLoading.show();
    const response: any = await window.miniCart?.addProductsToCart(productArr, false);

    if (response) {
      GlobalLoading.hide();
      if (response.code === '200') {
        // event tracking
        // addToCart event tracking
        const successData = productData.filter((data) => data && data.catalogNumber && !data.error);
        trackEvent({
          key: 'cart',
          action: 'addToCart',
          payload: {
            products: successData.map((item) => {
              return {
                name: item?.productTitle,
                catalogId: item.catalogNumber,
                quantity: item.quantity,
                uom: item.unitSize,
                placement: 'quick order',
                method: skuMethod[item.catalogNumber] ? skuMethod[item.catalogNumber] : 'quickordermanualentry',
              };
            }),
          },
        });
        const successCatalogs = response.data.success;
        if (
          errArr.length === 0 &&
          productArr.every((item) => successCatalogs.includes(item.catalogNumber.toUpperCase()))
        ) {
          router.push('/cart');
          return;
        }
        if (response.data.success.length > 0) {
          const failedCatalogIndexes = response.data.success.map((failedCatalog) => {
            return productData.findIndex((data) => data.catalogNumber === failedCatalog);
          });
          const updatedData = [...productData];
          const quantityData = [...productQuantity];
          failedCatalogIndexes.map((index) => {
            updatedData[index] = {};
            quantityData[index] = 0;
          });
          setProductData(updatedData);
          setProductQuantity(quantityData);
        }
        if (response.data.fail.length > 0) {
          setShowAlert(true);
          setErrorMsg(t('add.to.cart.sku.error').replace('%s', response.data.fail.join(',')));
        }
      } else {
        setShowAlert(true);
        setErrorMsg(response.message ? response.message : t('add.to.cart.error'));
      }
    }
  };
  const handleClickAway = () => {
    setShowAlert(false);
  };
  return (
    <>
      <div className="w-full sm:block">{t('manual.entry.subtitle')}</div>
      <div className="w-[90%] md:w-full">
        <ClickAwayListener onClickAway={handleClickAway}>
          <div>
            <Alert
              open={showAlert}
              onClose={() => setShowAlert(false)}
              type="error"
              white
              className="w-full self-start mb-4"
              title={t('error')}
              content={errorMsg}
            />
          </div>
        </ClickAwayListener>
        <div className="py-4 flex flex-col">
          <div className={`sm:hidden py-4 items-center text-sm text-gray-1100 font-normal ${gridClassName}`}>
            <span>
              {t('catalog.number')}
              <span className="text-red-500">* </span>
              <span>{t('required')}</span>
            </span>
            <span className="flex md:flex-col">
              {t('quantity')}
              <p>
                <i className="icon-16-info-mono mx-1 cursor-pointer text-info" />
                <span>{t('quantity.info')}</span>
              </p>
            </span>
            <span className="">{t('product.name')}</span>
            <span className="">{t('size')}</span>
            <span className="">{}</span>
          </div>
          <div className="flex-1 flex flex-col">
            {Array.from({ length: rowCount }).map((_, index) => (
              <ManualEntryRow
                key={index}
                keyVal={index}
                deleteRows={deleteData}
                productData={productData[index] ? productData[index] : {}}
                addProductData={addProductData}
                clearAllClicked={clearAllClicked}
                updateQuantity={updateQuantity}
                deleteRow={deleteRowIndex}
                quantityVal={productQuantity[index] ? productQuantity[index] : 1}
                //catalogVal={skuData[index]?.childSku ? skuData[index].childSku : ''}
                updateClearAll={() => setClearAll(false)}
              />
            ))}
          </div>
        </div>
        <div className="flex sm:flex-col sm:gap-2.5">
          {rowCount < 15 && (
            <Button type="outline" onClick={addMoreRows}>
              {t('add.btn')}
            </Button>
          )}

          <Button type="outline" className="ml-auto sm:ml-0" onClick={clearAll}>
            {t('clear.all')}
          </Button>
          <Button className="ml-2 sm:ml-0" disabled={!skuAdded} onClick={addToCart}>
            {t('add.to.cart')}
          </Button>
        </div>
      </div>
    </>
  );
});
export { ManualEntry };
